import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import api, { formatApiError } from "../../api";
import { Badge, Field, Input, Select } from "../common";
import SectionShell, { FieldGroup, FieldGroups } from "./SectionShell";

const MODELS = [
  "deepseek/deepseek-chat-v3-0324:free",
  "meta-llama/llama-3.3-70b-instruct:free",
  "google/gemini-2.0-flash-exp:free",
  "mistralai/mistral-7b-instruct:free",
];

/**
 * OpenRouter credentials and model choice.
 *
 * Not on the shared `useSectionForm` hook: the API key is write-only. The
 * server never returns it (only whether one is configured), so there is no
 * saved value to compare against and "dirty" has to mean *either* a typed key
 * *or* a changed model.
 */
export default function AiSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState(MODELS[0]);
  const [savedModel, setSavedModel] = useState(MODELS[0]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setModel(r.data.openrouter_model || MODELS[0]);
      setSavedModel(r.data.openrouter_model || MODELS[0]);
    }).catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  }, []);

  const dirty = apiKey.trim() !== "" || model !== savedModel;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = { openrouter_model: model };
      if (apiKey) payload.openrouter_api_key = apiKey;
      const r = await api.put("/settings", payload);
      setSettings(r.data);
      setSavedModel(r.data.openrouter_model || model);
      // Clear the box on success: leaving a secret sitting in an input after it
      // has been stored is a needless place for it to be read from.
      setApiKey("");
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } catch (e) {
      console.error("Saving AI settings failed:", e);
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const discard = () => { setApiKey(""); setModel(savedModel); setError(""); };

  return (
    <SectionShell
      icon={Sparkles} tone="glow"
      title={t("settings.ai")} desc={t("settings.aiDesc")}
      aside={settings && (
        <Badge value={settings.openrouter_configured ? "won" : "lost"}
          label={settings.openrouter_configured ? t("settings.configured") : t("settings.notConfigured")} />
      )}
      error={error}
      dirty={dirty} saving={saving} saved={saved}
      onSave={save} onDiscard={discard}>

      <FieldGroups>
        <FieldGroup label={t("settings.aiCredentials")}>
          <div className="max-w-md">
            <Field label={t("settings.apiKey")}>
              <Input data-testid="openrouter-key-input" type="password" autoComplete="off"
                value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                placeholder={settings?.openrouter_configured ? "•••••••••• (saved)" : "sk-or-v1-..."} />
            </Field>
            <p className="text-xs text-muted mt-1.5">
              {t("settings.aiKeyHint")}{" "}
              <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer"
                className="text-primary hover:underline">openrouter.ai/keys</a>
            </p>
          </div>
        </FieldGroup>

        <FieldGroup label={t("settings.aiModel")}>
          <div className="max-w-md">
            <Field label={t("settings.model")}>
              <Select data-testid="openrouter-model-select" value={model}
                onChange={(e) => setModel(e.target.value)}>
                {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
              </Select>
            </Field>
          </div>
        </FieldGroup>
      </FieldGroups>
    </SectionShell>
  );
}
